"use client";

import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  Archive,
  AlertTriangle,
  CalendarClock,
  CalendarRange,
  CheckCircle2,
  Clock3,
  Edit3,
  LockKeyhole,
  MapPin,
  PackageCheck,
  Plus,
  RotateCcw,
  TicketCheck,
  UserRound,
  UsersRound,
  WalletCards,
  X
} from "lucide-react";
import { useStudio } from "./StudioContext";
import type { Workshop, WorkshopInput, WorkshopRegistration } from "./types";
import { studioErrorCopy } from "./lib/api";
import { parseWorkshopSessionCost } from "./lib/workshop";

type WorkshopFilter = "upcoming" | "past" | "archived";

type WorkshopDraft = {
  title: string;
  description: string;
  eventDate: string;
  endsAt: string;
  registrationClosesAt: string;
  location: string;
  capacity: string;
  individualSessionCost: string;
  groupSessionCost: string;
  imageUrl: string;
  isActive: boolean;
};

const money = (value: number) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0
  }).format(value);

const workshopDate = (value: string) =>
  new Intl.DateTimeFormat("tr-TR", {
    timeZone: "Europe/Istanbul",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));

const shortWorkshopDate = (value: string) =>
  new Intl.DateTimeFormat("tr-TR", {
    timeZone: "Europe/Istanbul",
    day: "2-digit",
    month: "short"
  }).format(new Date(value));

function istanbulInputValue(value: Date | string) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  }).formatToParts(new Date(value));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}T${part("hour")}:${part("minute")}`;
}

function newWorkshopDraft(): WorkshopDraft {
  const start = new Date(Date.now() + 24 * 60 * 60 * 1000);
  start.setUTCMinutes(0, 0, 0);
  const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
  const close = new Date(start.getTime() - 24 * 60 * 60 * 1000);
  return {
    title: "",
    description: "",
    eventDate: istanbulInputValue(start),
    endsAt: istanbulInputValue(end),
    registrationClosesAt: istanbulInputValue(close),
    location: "The Heal Hub Stüdyo",
    capacity: "8",
    individualSessionCost: "1",
    groupSessionCost: "1",
    imageUrl: "",
    isActive: true
  };
}

function workshopDraft(workshop: Workshop): WorkshopDraft {
  return {
    title: workshop.title,
    description: workshop.description ?? "",
    eventDate: istanbulInputValue(workshop.eventDate),
    endsAt: workshop.endsAt ? istanbulInputValue(workshop.endsAt) : "",
    registrationClosesAt: workshop.registrationClosesAt
      ? istanbulInputValue(workshop.registrationClosesAt)
      : "",
    location: workshop.location ?? "",
    capacity: workshop.capacity == null ? "" : String(workshop.capacity),
    individualSessionCost: String(workshop.individualSessionCost ?? 1),
    groupSessionCost: String(workshop.groupSessionCost ?? 1),
    imageUrl: workshop.imageUrl ?? "",
    isActive: workshop.isActive
  };
}

function workshopState(workshop: Workshop) {
  const now = Date.now();
  if (!workshop.isActive) return { code: "archived", label: "Yayında değil" };
  if (new Date(workshop.eventDate).getTime() <= now) return { code: "past", label: "Tamamlandı" };
  if (workshop.capacity != null && workshop.registeredCount >= workshop.capacity) {
    return { code: "full", label: "Kontenjan dolu" };
  }
  if (
    workshop.registrationClosesAt &&
    new Date(workshop.registrationClosesAt).getTime() <= now
  ) {
    return { code: "closed", label: "Kayıt kapandı" };
  }
  return { code: "open", label: "Kayıt açık" };
}

const participationCategory = (registration: WorkshopRegistration) =>
  registration.packageCategoryCode === "INDIVIDUAL" ? "Bireysel paket" : "Grup paketi";

function WorkshopCancellationDialog({
  workshop,
  registration,
  returnFocusTo,
  onClose,
  onCancelled
}: {
  workshop: Workshop;
  registration: WorkshopRegistration;
  returnFocusTo: HTMLButtonElement | null;
  onClose: () => void;
  onCancelled: (message: string) => void;
}) {
  const { cancelWorkshopParticipation } = useStudio();
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState("");
  const dialogRef = useRef<HTMLElement>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const cancellingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const sessionCost = registration.sessionCost ?? 0;
  onCloseRef.current = onClose;

  useEffect(() => {
    const returnTarget = returnFocusTo;
    reasonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (!cancellingRef.current) onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;

      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          "button, textarea, input, select, a[href], [tabindex]"
        )
      ).filter(
        (element) =>
          !element.matches(":disabled") &&
          !element.hasAttribute("hidden") &&
          element.getAttribute("aria-hidden") !== "true" &&
          element.tabIndex >= 0
      );
      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!focusable.includes(active as HTMLElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      queueMicrotask(() => {
        if (returnTarget?.isConnected) returnTarget.focus();
      });
    };
  }, [returnFocusTo]);

  function requestClose() {
    if (!cancellingRef.current) onClose();
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedReason = reason.trim();
    if (!trimmedReason || !confirmed || cancellingRef.current) return;

    cancellingRef.current = true;
    setCancelling(true);
    setError("");
    try {
      await cancelWorkshopParticipation(registration, trimmedReason);
      cancellingRef.current = false;
      setCancelling(false);
      onCancelled(`Katılım iptal edildi; ${sessionCost} hak aynı pakete iade edildi.`);
      onClose();
    } catch (cancellationError) {
      setError(
        studioErrorCopy(
          cancellationError,
          "Katılım iptal edilemedi. Güncel katılım bilgilerini kontrol edip yeniden deneyin."
        )
      );
      cancellingRef.current = false;
      setCancelling(false);
    }
  }

  return (
    <>
      <button
        className="modal-scrim workshop-cancellation-scrim"
        onClick={requestClose}
        disabled={cancelling}
        aria-label="Katılım iptal penceresini kapat"
      />
      <section
        ref={dialogRef}
        className="modal-card workshop-cancellation-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="workshop-cancellation-title"
      >
        <div className="drawer-head">
          <div>
            <span className="eyebrow">Stüdyo istisnası</span>
            <h2 id="workshop-cancellation-title">Katılımı iptal et</h2>
          </div>
          <button className="icon-button" type="button" onClick={requestClose} disabled={cancelling} aria-label="Kapat">
            <X size={20} />
          </button>
        </div>

        <div className="workshop-cancellation-warning">
          <AlertTriangle size={21} />
          <span>
            <strong>Müşteri bu işlemi mobil uygulamadan geri alamaz.</strong>
            <small>Katılım iptal edilir, kontenjan açılır ve düşülen haklar aynı pakete iade edilir.</small>
          </span>
        </div>

        <dl className="workshop-cancellation-facts">
          <div><dt>Müşteri</dt><dd>{registration.name}</dd></div>
          <div><dt>Atölye</dt><dd>{workshop.title}</dd></div>
          <div><dt>Paket</dt><dd>{registration.packageName || "Paket bilgisi bulunamadı"}</dd></div>
          <div className="refund"><dt>İade</dt><dd>{sessionCost} hak</dd></div>
        </dl>

        <form onSubmit={submit}>
          <label className="field workshop-cancellation-reason">
            <span>İptal gerekçesi <i>zorunlu</i></span>
            <textarea
              ref={reasonRef}
              aria-label="İptal gerekçesi"
              required
              maxLength={500}
              rows={4}
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                setError("");
              }}
              placeholder="Müşteri kaydında görülecek stüdyo gerekçesini yazın…"
            />
          </label>
          <label className="workshop-cancellation-confirmation">
            <input
              type="checkbox"
              aria-label="İşlemi ve hak iadesini onaylıyorum"
              checked={confirmed}
              onChange={(event) => {
                setConfirmed(event.target.checked);
                setError("");
              }}
            />
            <span>
              <strong>İşlemi ve hak iadesini onaylıyorum</strong>
              <small>İade hareketi mevcut pakete yazılır; yeni veya farklı paket oluşturulmaz.</small>
            </span>
          </label>
          {error && <div className="form-error" role="alert">{error}</div>}
          <div className="modal-actions">
            <button className="button secondary" type="button" onClick={requestClose} disabled={cancelling}>
              Vazgeç
            </button>
            <button
              className="button workshop-cancellation-submit"
              disabled={!reason.trim() || !confirmed || cancelling}
            >
              <RotateCcw size={17} />
              {cancelling ? "İptal ediliyor…" : `İptal et ve ${sessionCost} hakkı iade et`}
            </button>
          </div>
        </form>
      </section>
    </>
  );
}

function WorkshopEditor({
  workshop,
  managementReady,
  onClose,
  onSaved
}: {
  workshop?: Workshop;
  managementReady: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const { createWorkshop, updateWorkshop } = useStudio();
  const [draft, setDraft] = useState<WorkshopDraft>(() =>
    workshop ? workshopDraft(workshop) : newWorkshopDraft()
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const sessionCostsLocked = Boolean(workshop?.sessionPricingLockedAtUtc);

  const update = <K extends keyof WorkshopDraft>(key: K, value: WorkshopDraft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setError("");
  };

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!managementReady) {
      setError("Paket hakları kurulumu tamamlanmadan atölye kaydedilemez.");
      return;
    }
    const individualSessionCost = parseWorkshopSessionCost(draft.individualSessionCost);
    const groupSessionCost = parseWorkshopSessionCost(draft.groupSessionCost);
    const capacity = draft.capacity.trim() ? Number(draft.capacity) : undefined;
    if (!draft.title.trim() || !draft.eventDate) {
      setError("Başlık ve başlangıç tarihi alanlarını kontrol edin.");
      return;
    }
    if (individualSessionCost == null || groupSessionCost == null) {
      setError("Bireysel ve grup hak maliyetleri 1 ile 100 arasında tam sayı olmalıdır.");
      return;
    }
    if (capacity != null && (!Number.isInteger(capacity) || capacity < 1)) {
      setError("Kontenjan boş bırakılmalı veya 1'den büyük tam sayı olmalıdır.");
      return;
    }
    if (draft.endsAt && draft.endsAt <= draft.eventDate) {
      setError("Bitiş tarihi başlangıçtan sonra olmalıdır.");
      return;
    }
    if (draft.registrationClosesAt && draft.registrationClosesAt > draft.eventDate) {
      setError("Son kayıt tarihi atölye başlangıcından sonra olamaz.");
      return;
    }

    const payload: WorkshopInput = {
      title: draft.title.trim(),
      description: draft.description.trim() || undefined,
      eventDate: draft.eventDate,
      endsAt: draft.endsAt || undefined,
      registrationClosesAt: draft.registrationClosesAt || undefined,
      location: draft.location.trim() || undefined,
      capacity,
      participationModeCode: "PACKAGE_SESSION",
      individualSessionCost,
      groupSessionCost,
      imageUrl: draft.imageUrl.trim() || undefined,
      isActive: draft.isActive
    };

    setSaving(true);
    try {
      if (workshop) await updateWorkshop(workshop, payload);
      else await createWorkshop(payload);
      onSaved(workshop ? "Atölye bilgileri güncellendi." : "Yeni atölye programa eklendi.");
      onClose();
    } catch (submitError) {
      setError(studioErrorCopy(submitError, "Atölye kaydedilemedi. Bilgileri kontrol edip yeniden deneyin."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <button className="modal-scrim" onClick={onClose} aria-label="Atölye penceresini kapat" />
      <section className="modal-card modal-wide workshop-editor" role="dialog" aria-modal="true" aria-labelledby="workshop-editor-title">
        <div className="drawer-head">
          <div>
            <span className="eyebrow">Programı tasarla</span>
            <h2 id="workshop-editor-title">{workshop ? "Atölyeyi düzenle" : "Yeni atölye"}</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Kapat"><X size={20} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="workshop-form-grid">
            <label className="field full"><span>Atölye adı</span><input autoFocus required maxLength={200} value={draft.title} onChange={(event) => update("title", event.target.value)} placeholder="Örn. Nefesinle Alan Aç" /></label>
            <label className="field full"><span>Açıklama <i>isteğe bağlı</i></span><textarea rows={4} value={draft.description} onChange={(event) => update("description", event.target.value)} placeholder="Katılımcıların mobil uygulamada göreceği kısa açıklama…" /></label>
            <label className="field"><span>Başlangıç</span><input type="datetime-local" required value={draft.eventDate} onChange={(event) => update("eventDate", event.target.value)} /></label>
            <label className="field"><span>Bitiş <i>isteğe bağlı</i></span><input type="datetime-local" value={draft.endsAt} onChange={(event) => update("endsAt", event.target.value)} /></label>
            <label className="field"><span>Son kayıt zamanı <i>isteğe bağlı</i></span><input type="datetime-local" value={draft.registrationClosesAt} onChange={(event) => update("registrationClosesAt", event.target.value)} /></label>
            <label className="field"><span>Konum</span><input maxLength={200} value={draft.location} onChange={(event) => update("location", event.target.value)} placeholder="The Heal Hub Stüdyo" /></label>
            <label className="field"><span>Kontenjan <i>boş = sınırsız</i></span><input type="number" min="1" max="10000" step="1" value={draft.capacity} onChange={(event) => update("capacity", event.target.value)} /></label>
            <label className="field full"><span>Kapak görseli bağlantısı <i>isteğe bağlı</i></span><input type="url" maxLength={500} value={draft.imageUrl} onChange={(event) => update("imageUrl", event.target.value)} placeholder="https://…" /></label>
            <fieldset className={`workshop-entitlement-costs full ${sessionCostsLocked ? "locked" : ""}`}>
              <legend>Hak karşılığı</legend>
              <label className="workshop-cost-field">
                <span><UserRound size={17} /> Bireysel paketten düşecek hak</span>
                <span className="workshop-cost-input"><input required type="number" inputMode="numeric" min="1" max="100" step="1" disabled={sessionCostsLocked} value={draft.individualSessionCost} onChange={(event) => update("individualSessionCost", event.target.value)} /><i>hak</i></span>
              </label>
              <label className="workshop-cost-field">
                <span><UsersRound size={17} /> Grup paketinden düşecek hak</span>
                <span className="workshop-cost-input"><input required type="number" inputMode="numeric" min="1" max="100" step="1" disabled={sessionCostsLocked} value={draft.groupSessionCost} onChange={(event) => update("groupSessionCost", event.target.value)} /><i>hak</i></span>
              </label>
              <p className="workshop-cost-explainer"><WalletCards size={16} /> Müşteri mobil uygulamada hangi uygun paketini kullanacağını seçer.</p>
              {sessionCostsLocked && <p className="workshop-cost-lock"><LockKeyhole size={15} /> İlk katılımdan sonra sabitlendi</p>}
            </fieldset>
          </div>
          <label className="workshop-publish-toggle">
            <input type="checkbox" checked={draft.isActive} onChange={(event) => update("isActive", event.target.checked)} />
            <span><strong>Mobil uygulamada yayınla</strong><small>Kapalıysa atölye taslak olarak saklanır ve müşteri kaydı alınmaz.</small></span>
          </label>
          {error && <div className="form-error">{error}</div>}
          <div className="modal-actions">
            <button type="button" className="button secondary" onClick={onClose}>Vazgeç</button>
            <button className="button primary" disabled={saving || !managementReady}>{saving ? "Kaydediliyor…" : workshop ? "Değişiklikleri kaydet" : "Atölyeyi oluştur"}</button>
          </div>
        </form>
      </section>
    </>
  );
}

function WorkshopDetail({
  workshop,
  onClose,
  onEdit,
  onArchive,
  onToast,
  archiving,
  managementReady
}: {
  workshop: Workshop;
  onClose: () => void;
  onEdit: () => void;
  onArchive: () => void;
  onToast: (message: string) => void;
  archiving: boolean;
  managementReady: boolean;
}) {
  const state = workshopState(workshop);
  const contractMissing =
    workshop.participationModeCode !== "PACKAGE_SESSION" &&
    workshop.participationModeCode !== "LEGACY_FEE";
  const [cancellationTarget, setCancellationTarget] = useState<WorkshopRegistration | null>(null);
  const cancellationTriggerRef = useRef<HTMLButtonElement | null>(null);
  return (
    <>
      <button
        className="modal-scrim"
        onClick={() => {
          if (!cancellationTarget) onClose();
        }}
        aria-label="Atölye detayını kapat"
        aria-hidden={cancellationTarget ? true : undefined}
        tabIndex={cancellationTarget ? -1 : undefined}
        disabled={Boolean(cancellationTarget)}
      />
      <aside
        className="detail-drawer workshop-detail-drawer"
        inert={cancellationTarget ? true : undefined}
        aria-hidden={cancellationTarget ? true : undefined}
      >
        <div className="drawer-head">
          <div><span className="eyebrow">Atölye detayı</span><h2>{workshop.title}</h2></div>
          <button className="icon-button" onClick={onClose} aria-label="Kapat"><X size={20} /></button>
        </div>
        <div className={`workshop-detail-hero ${state.code}`}>
          <span className="workshop-state-dot" />
          <strong>{state.label}</strong>
          <span>{workshopDate(workshop.eventDate)}</span>
        </div>
        <div className="workshop-detail-facts">
          <div><CalendarClock size={18} /><span><small>Program</small><strong>{workshop.endsAt ? `${workshopDate(workshop.eventDate)} – ${workshopDate(workshop.endsAt)}` : workshopDate(workshop.eventDate)}</strong></span></div>
          <div><MapPin size={18} /><span><small>Konum</small><strong>{workshop.location || "Belirtilmedi"}</strong></span></div>
          <div><UsersRound size={18} /><span><small>Doluluk</small><strong>{workshop.registeredCount} / {workshop.capacity ?? "∞"}</strong></span></div>
          {workshop.participationModeCode === "PACKAGE_SESSION" ? (
            <>
              <div className="workshop-entitlement-fact"><UserRound size={18} /><span><small>Bireysel paket</small><strong>{workshop.individualSessionCost} hak</strong></span></div>
              <div className="workshop-entitlement-fact"><UsersRound size={18} /><span><small>Grup paketi</small><strong>{workshop.groupSessionCost} hak</strong></span></div>
              <div className="workshop-entitlement-fact"><LockKeyhole size={18} /><span><small>Hak karşılığı</small><strong>{workshop.sessionPricingLockedAtUtc ? "İlk katılımda sabitlendi" : "İlk katılıma kadar düzenlenebilir"}</strong></span></div>
            </>
          ) : workshop.participationModeCode === "LEGACY_FEE" ? (
            <div className="workshop-entitlement-fact"><WalletCards size={18} /><span><small>Eski katılım ücreti</small><strong>{money(workshop.amount)}</strong></span></div>
          ) : (
            <div className="workshop-entitlement-fact"><LockKeyhole size={18} /><span><small>Katılım bilgileri</small><strong>Backend güncellemesi gerekli</strong></span></div>
          )}
        </div>
        {workshop.participationModeCode === "LEGACY_FEE" && <span className="workshop-legacy-badge">Eski ücretli atölye · salt okunur</span>}
        {workshop.description && <p className="workshop-detail-description">{workshop.description}</p>}
        <div className="workshop-registration-head">
          <div><span className="eyebrow">Katılımcılar</span><h3>{workshop.registrations.length} kayıt hareketi</h3></div>
          <span>{workshop.registeredCount} aktif</span>
        </div>
        <div className="workshop-registration-list">
          {workshop.registrations.map((registration) => {
            const isLegacy = registration.isLegacy;
            const registrationContractMissing = contractMissing || registration.isLegacy == null;
            const isCancelled = registration.participationStatusCode === "CANCELLED_BY_STUDIO";
            const canCancel =
              !registrationContractMissing &&
              !isLegacy &&
              !isCancelled &&
              registration.participationStatusCode === "REGISTERED" &&
              Boolean(registration.participationId && registration.participationVersion);
            return (
              <article
                className={`workshop-registration ${registrationContractMissing ? "contract-missing" : isLegacy ? "legacy" : "modern"} ${isCancelled ? "cancelled" : ""}`}
                key={registration.id}
              >
                <div className="workshop-registration-person">
                  <span className="avatar">{registration.name.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toLocaleUpperCase("tr-TR")}</span>
                  <span>
                    <strong>{registration.name}</strong>
                    <small>{registration.phone || "Telefon belirtilmedi"} · {workshopDate(registration.registeredAt)}</small>
                  </span>
                  <span className={`workshop-participation-status ${isCancelled ? "cancelled" : registrationContractMissing ? "contract-missing" : isLegacy ? "legacy" : "registered"}`}>
                    {isCancelled ? "Stüdyo tarafından iptal edildi" : registrationContractMissing ? "Bilgi bekleniyor" : isLegacy ? "Eski kayıt" : "Kayıtlı"}
                  </span>
                </div>

                {registrationContractMissing ? (
                  <p className="workshop-registration-legacy">Backend güncellemesi gerekli · paket ve hak bilgileri henüz alınamadı</p>
                ) : isLegacy ? (
                  <p className="workshop-registration-legacy">Eski kayıt · paket hareketi yok</p>
                ) : (
                  <div className="workshop-registration-entitlement">
                    <span><PackageCheck size={16} /><small>Seçilen paket</small><strong>{registration.packageName || "Paket bilgisi bulunamadı"}</strong></span>
                    <span><UserRound size={16} /><small>Paket türü</small><strong>{participationCategory(registration)}</strong></span>
                    <span><TicketCheck size={16} /><small>Katılım hakkı</small><strong>{registration.sessionCost ?? 0} hak düşüldü</strong></span>
                  </div>
                )}

                {isCancelled && (
                  <div className="workshop-registration-cancellation">
                    <RotateCcw size={16} />
                    <span>
                      <strong>{registration.sessionCost ?? 0} hak aynı pakete iade edildi</strong>
                      <small>
                        {registration.cancelledAtUtc ? workshopDate(registration.cancelledAtUtc) : "Tarih belirtilmedi"}
                        {registration.cancelledBy ? ` · ${registration.cancelledBy}` : ""}
                      </small>
                      <em>{registration.cancellationReason || "İptal gerekçesi belirtilmedi"}</em>
                    </span>
                  </div>
                )}

                {canCancel && (
                  <div className="workshop-registration-actions">
                    <button
                      type="button"
                      className="workshop-participation-cancel"
                      onClick={(event) => {
                        cancellationTriggerRef.current = event.currentTarget;
                        setCancellationTarget(registration);
                      }}
                      disabled={!managementReady}
                      aria-label={`${registration.name} katılımını iptal et`}
                    >
                      <RotateCcw size={15} /> Katılımı iptal et
                    </button>
                  </div>
                )}
              </article>
            );
          })}
          {!workshop.registrations.length && <div className="empty-state compact"><UserRound size={22} /><strong>Henüz katılımcı yok</strong><span>Mobil uygulamadan gelen kayıtlar burada görünecek.</span></div>}
        </div>
        <div className="drawer-actions">
          <button className="button secondary wide" onClick={onEdit} disabled={!workshop.version || !managementReady || workshop.participationModeCode === "LEGACY_FEE"}><Edit3 size={17} /> Atölyeyi düzenle</button>
          {workshop.isActive && <button className="button danger-outline wide" onClick={onArchive} disabled={archiving || !workshop.version || !managementReady || workshop.participationModeCode === "LEGACY_FEE"}><Archive size={17} /> {archiving ? "Yayından kaldırılıyor…" : "Yayından kaldır"}</button>}
        </div>
      </aside>
      {cancellationTarget && (
        <WorkshopCancellationDialog
          workshop={workshop}
          registration={cancellationTarget}
          returnFocusTo={cancellationTriggerRef.current}
          onClose={() => setCancellationTarget(null)}
          onCancelled={onToast}
        />
      )}
    </>
  );
}

export function WorkshopPage({ onToast }: { onToast: (message: string) => void }) {
  const { workshops, workshopManagementReady, archiveWorkshop } = useStudio();
  const [filter, setFilter] = useState<WorkshopFilter>("upcoming");
  const [editor, setEditor] = useState<Workshop | "new" | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);
  const now = Date.now();

  const upcoming = workshops.filter((item) => item.isActive && new Date(item.eventDate).getTime() > now);
  const past = workshops.filter((item) => item.isActive && new Date(item.eventDate).getTime() <= now);
  const archived = workshops.filter((item) => !item.isActive);
  const filtered = filter === "upcoming" ? upcoming : filter === "past" ? past : archived;
  const selected = workshops.find((item) => item.id === selectedId) ?? null;

  const openRegistrationCount = useMemo(
    () => upcoming.filter((item) => workshopState(item).code === "open").length,
    [upcoming]
  );
  const attendeeCount = upcoming.reduce((sum, item) => sum + item.registeredCount, 0);
  const nextWorkshop = [...upcoming].sort((left, right) => left.eventDate.localeCompare(right.eventDate))[0];

  async function archiveSelected() {
    if (!selected || !window.confirm(`“${selected.title}” mobil uygulamada yayından kaldırılsın mı? Mevcut katılımcı kayıtları silinmez.`)) return;
    setArchiving(true);
    try {
      await archiveWorkshop(selected);
      setSelectedId(null);
      onToast("Atölye yayından kaldırıldı; katılımcı geçmişi korundu.");
    } catch (error) {
      onToast(studioErrorCopy(error, "Atölye yayından kaldırılamadı."));
    } finally {
      setArchiving(false);
    }
  }

  return (
    <div className="workshop-page">
      <div className="page-heading workshop-page-heading">
        <div><span className="eyebrow">Etkinlik programı</span><h1>Atölyeler</h1><p>Yeni atölyeleri planlayın, mobil kayıtları ve kontenjanı tek ekrandan izleyin.</p></div>
        <button className="button primary" onClick={() => setEditor("new")} disabled={!workshopManagementReady}><Plus size={18} /> Yeni atölye</button>
      </div>

      {!workshopManagementReady && (
        <div className="workshop-setup-callout">
          <CalendarRange size={22} />
          <span><strong>Atölye hak yönetimi için kurulum bekleniyor.</strong><small>Ortak SQL ve mobil paket seçimi hazır olduğunda oluşturma ve düzenleme açılır; mevcut atölyeler salt okunur kalır.</small></span>
        </div>
      )}

      <section className="workshop-overview">
        <div className="workshop-next-card">
          <span className="eyebrow">Sıradaki buluşma</span>
          {nextWorkshop ? (
            <>
              <strong>{nextWorkshop.title}</strong>
              <span>{workshopDate(nextWorkshop.eventDate)} · {nextWorkshop.location || "Konum belirtilmedi"}</span>
              <button onClick={() => setSelectedId(nextWorkshop.id)}>Programı aç</button>
            </>
          ) : (
            <><strong>Takvimde yaklaşan atölye yok</strong><span>Yeni bir program oluşturarak mobil kaydı açabilirsiniz.</span></>
          )}
        </div>
        <div className="workshop-metric"><TicketCheck size={21} /><span><strong>{openRegistrationCount}</strong><small>Kayıt alan atölye</small></span></div>
        <div className="workshop-metric"><UsersRound size={21} /><span><strong>{attendeeCount}</strong><small>Yaklaşan katılımcı</small></span></div>
        <div className="workshop-metric"><CalendarClock size={21} /><span><strong>{upcoming.length}</strong><small>Yaklaşan program</small></span></div>
      </section>

      <div className="workshop-toolbar">
        <div className="segmented workshop-filters" role="tablist" aria-label="Atölye filtresi">
          <button className={filter === "upcoming" ? "active" : ""} onClick={() => setFilter("upcoming")}>Yaklaşan <span>{upcoming.length}</span></button>
          <button className={filter === "past" ? "active" : ""} onClick={() => setFilter("past")}>Geçmiş <span>{past.length}</span></button>
          <button className={filter === "archived" ? "active" : ""} onClick={() => setFilter("archived")}>Yayında değil <span>{archived.length}</span></button>
        </div>
      </div>

      <section className="workshop-grid">
        {filtered.map((workshop) => {
          const state = workshopState(workshop);
          const occupancy = workshop.capacity
            ? Math.min(100, Math.round((workshop.registeredCount / workshop.capacity) * 100))
            : 0;
          return (
            <article className="workshop-card" key={workshop.id}>
              <div className="workshop-card-visual" style={workshop.imageUrl ? { backgroundImage: `linear-gradient(120deg, rgba(8, 58, 50, .24), rgba(8, 58, 50, .8)), url(${JSON.stringify(workshop.imageUrl)})` } : undefined}>
                {!workshop.imageUrl && <CalendarRange size={38} strokeWidth={1.4} />}
                <span className={`workshop-status ${state.code}`}><i />{state.label}</span>
                <span className="workshop-date-ticket"><strong>{shortWorkshopDate(workshop.eventDate).split(" ")[0]}</strong><small>{shortWorkshopDate(workshop.eventDate).split(" ").slice(1).join(" ")}</small></span>
              </div>
              <div className="workshop-card-body">
                <div><h2>{workshop.title}</h2><p>{workshop.description || "Atölye açıklaması henüz eklenmedi."}</p></div>
                <div className="workshop-card-meta"><span><Clock3 size={15} />{workshopDate(workshop.eventDate)}</span><span><MapPin size={15} />{workshop.location || "Konum belirtilmedi"}</span></div>
                <div className="workshop-capacity">
                  <span><strong>{workshop.registeredCount}</strong> / {workshop.capacity ?? "∞"} katılımcı</span>
                  {workshop.participationModeCode === "PACKAGE_SESSION" ? (
                    <strong className="workshop-card-entitlement">Bireysel {workshop.individualSessionCost} hak · Grup {workshop.groupSessionCost} hak</strong>
                  ) : workshop.participationModeCode === "LEGACY_FEE" ? (
                    <span className="workshop-card-legacy"><i className="workshop-legacy-badge">Eski ücretli atölye</i><strong>{money(workshop.amount)}</strong></span>
                  ) : (
                    <strong className="workshop-card-entitlement">Katılım bilgileri için backend güncellemesi gerekli</strong>
                  )}
                  <div><i style={{ width: `${occupancy}%` }} /></div>
                </div>
                <button className="workshop-card-action" onClick={() => setSelectedId(workshop.id)}>Detayları ve katılımcıları aç <span>→</span></button>
              </div>
            </article>
          );
        })}
        {!filtered.length && (
          <div className="empty-state workshop-empty"><CheckCircle2 size={28} /><strong>{filter === "upcoming" ? "Yaklaşan atölye bulunmuyor" : filter === "past" ? "Geçmiş atölye bulunmuyor" : "Yayından kaldırılmış atölye yok"}</strong><span>Bu bölümdeki programlar oluşturuldukça burada listelenecek.</span></div>
        )}
      </section>

      {selected && (
        <WorkshopDetail
          workshop={selected}
          onClose={() => setSelectedId(null)}
          onEdit={() => { setEditor(selected); setSelectedId(null); }}
          onArchive={archiveSelected}
          onToast={onToast}
          archiving={archiving}
          managementReady={workshopManagementReady}
        />
      )}
      {editor && (
        <WorkshopEditor
          workshop={editor === "new" ? undefined : editor}
          managementReady={workshopManagementReady}
          onClose={() => setEditor(null)}
          onSaved={onToast}
        />
      )}
    </div>
  );
}
